import authService from '../services/auth-service';
import cuid from 'cuid';
import { countryCurrencyMap } from './init-data';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const cartService = {
  getCart: async (userId) => {
    try {
      const token = authService.getToken();
      if (!token) {
        return [];
      }
      
      const response = await fetch(`${API_URL}/api/_find`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          selector: {
            type: 'cart_item',
            user_id: userId
          }
        })
      });
      
      if (!response.ok) {
        console.error('Failed to fetch cart:', response.status);
        return [];
      }
      
      const result = await response.json();
      if (!result?.docs) return [];
      
      const cartItems = result.docs.filter(item => !item.deleted);
      
      const enrichedItems = await Promise.all(cartItems.map(async (item) => {
        try {
          const productResponse = await fetch(`${API_URL}/api/${item.product_id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (!productResponse.ok) {
            return {
              cartId: item._id,
              productId: item.product_id,
              name: 'Product not found',
              price: 0,
              quantity: item.quantity
            };
          }
          
          const product = await productResponse.json();
          const userCountry = sessionStorage.getItem('userCountry') || 'US';
          const currencyCode = countryCurrencyMap[userCountry] || 'USD';
          const price = product.pricing?.[currencyCode] || product.price || 0;
          
          return {
            cartId: item._id,
            productId: product._id,
            name: product.name,
            price,
            quantity: item.quantity,
            image_url: product.image_url,
            is_organic: product.is_organic
          };
        } catch (error) {
          return {
            cartId: item._id,
            productId: item.product_id,
            name: 'Product not found',
            price: 0,
            quantity: item.quantity
          };
        }
      }));
      
      return enrichedItems;
    } catch (error) {
      console.error('Error getting cart:', error);
      return [];
    }
  },
  
  addToCart: async (userId, productId, quantity, isPresent) => {
    try {
      const token = authService.getToken();
      if (!token) {
        throw new Error('Please log in to add items to cart');
      }
      
      if (isPresent) {
        try {
          const response = await fetch(`${API_URL}/api/_find`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              selector: {
                type: 'cart_item',
                user_id: userId,
                product_id: productId
              }
            })
          });
          
          if (response.ok) {
            const result = await response.json();
            if (result.docs?.length > 0) {
              const cartItem = result.docs[0];
              cartItem.quantity += quantity;
              cartItem.updatedAt = new Date().toISOString();
              
              const updateResponse = await fetch(`${API_URL}/api/${cartItem._id}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(cartItem)
              });
              
              if (updateResponse.ok) {
                window.dispatchEvent(new CustomEvent('cartUpdated'));
                return { success: true };
              }
            }
          }
        } catch (err) {
          console.error('Error updating existing cart item:', err);
        }
      }
      
      const cartItem = {
        _id: cuid(),
        type: 'cart_item',
        user_id: userId,
        product_id: productId,
        quantity,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      const response = await fetch(`${API_URL}/api/${cartItem._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(cartItem)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to add item: ${response.status}`);
      }
      
      window.dispatchEvent(new CustomEvent('cartUpdated'));
      return { success: true };
    } catch (error) {
      console.error('Error adding to cart:', error);
      if (error.message?.includes('log in')) throw error;
      throw new Error('Failed to add item to cart. Please try again.');
    }
  },
  
  removeFromCart: async (productId, userId) => {
    try {
      const token = authService.getToken();
      if (!token) {
        throw new Error('Please log in to remove items from cart');
      }
      
      const response = await fetch(`${API_URL}/api/_find`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          selector: {
            type: 'cart_item',
            user_id: userId,
            product_id: productId
          }
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to find cart item');
      }
      
      const result = await response.json();
      if (!result.docs?.length) {
        throw new Error('Cart item not found');
      }
      
      const cartItem = result.docs[0];
      cartItem.deleted = true;
      cartItem.updatedAt = new Date().toISOString();
      
      const updateResponse = await fetch(`${API_URL}/api/${cartItem._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(cartItem)
      });
      
      if (!updateResponse.ok) {
        throw new Error('Failed to remove item');
      }
      
      window.dispatchEvent(new CustomEvent('cartUpdated'));
      return { success: true };
    } catch (error) {
      console.error('Error removing from cart:', error);
      if (error.message?.includes('log in')) throw error;
      throw new Error('Failed to remove item from cart. Please try again.');
    }
  },
  
  buyCart: async (userId, address) => {
    try {
      const token = authService.getToken();
      if (!token) {
        throw new Error('Please log in to complete purchase');
      }
      
      const cartItems = await cartService.getCart(userId);
      if (cartItems.length === 0) {
        throw new Error('Cart is empty');
      }
      
      const totalAmount = cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
      const timestamp = new Date().toISOString();
      
      // Check if this is a trial order
      const isTrial = address.isTrial || false;
      const trialSubscriptionId = address.trialSubscriptionId || null;
      
      const order = {
        _id: cuid(),
        type: 'order',
        customerId: userId,
        customerName: sessionStorage.getItem("customerName") || "Customer",
        items: cartItems,
        totalAmount: isTrial ? 0 : totalAmount,
        status: isTrial ? 'confirmed' : 'paid',
        paymentStatus: isTrial ? 'free_trial' : (address.paymentStatus || 'Paid'),
        isTrial: isTrial,
        trialSubscriptionId: trialSubscriptionId,
        shippingAddress: {
          fullName: address.fullName,
          addressLine1: address.addressLine1,
          addressLine2: address.addressLine2,
          city: address.city,
          state: address.state,
          pincode: address.pincode,
          phone: address.phone
        },
        deliveryDate: address.deliveryDate,
        deliveryTimeSlot: address.deliveryTimeSlot,
        date: timestamp,
        createdAt: timestamp,
        updatedAt: timestamp
      };
      
      const response = await fetch(`${API_URL}/api/${order._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(order)
      });
      
      if (!response.ok) {
        throw new Error('Failed to create order');
      }
      
      for (const item of cartItems) {
        await cartService.removeFromCart(item.productId, userId);
      }
      
      window.dispatchEvent(new CustomEvent('cartUpdated'));
      return { success: true, orderId: order._id };
    } catch (error) {
      console.error('Error buying cart:', error);
      if (error.message?.includes('log in')) throw error;
      throw new Error('Failed to complete purchase. Please try again.');
    }
  }
};

export default cartService;
